// LOC2, UX128 (docs/build-briefs/UX128-bars-out-of-devices.md): Settings > Location > Locations. The customer's locations, and
// (admin.location.manage) a plain form to add one. A new location starts clean and takes the customer's tax rate, time zone
// and tip presets. It was drawn inside Settings > Devices by ui/src/tablets.ts until UX128 moved it here.
//
// Admin surface: typing is fine here. The idempotency key is made when the form is first sent and kept while the same
// location is retried, so a lost answer can never add two. The list is read from GET .../locations (admin.user.view); the
// form draws only for a session holding admin.location.manage (resolved permissions, never a role name). All text is
// set with textContent.

import { bindAutofillSelect } from "./autofill.ts";
import { addLocation, listTenantLocations, type TenantLocationNameDto } from "./api.ts";

export interface LocationsSectionDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds admin.location.manage, so the card offers Add a location. */
  canAddLocations: () => boolean;
  /** The session holds admin.user.view, the permission GET .../locations requires. */
  canListLocations: () => boolean;
  /** True while the session this section serves is still the live one. */
  isCurrent: () => boolean;
  newKey: () => string;
  /** LOC3: called after a location is added, so the Address card's location list follows. */
  onLocationsChanged?: () => void;
}

export interface LocationsSection {
  /** Reads the locations and draws the card; draws nothing for a session with neither permission. */
  refresh: () => Promise<void>;
  /** Forgets everything; the next employee starts clean. */
  reset: () => void;
}

interface ElOpts {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: ElOpts = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.cls !== undefined) node.className = opts.cls;
  if (opts.text !== undefined) node.textContent = opts.text;
  if (opts.testid !== undefined) node.dataset["testid"] = opts.testid;
  for (const [name, value] of Object.entries(opts.attrs ?? {})) {
    node.setAttribute(name, value);
  }
  for (const child of children) node.append(child);
  return node;
}

const EMPTY_DRAFT = { name: "", line1: "", city: "", state: "", postal: "" };

export function mountLocationsSection(
  root: HTMLElement,
  deps: LocationsSectionDeps,
): LocationsSection {
  let locations: TenantLocationNameDto[] = [];
  let locationDraft = { ...EMPTY_DRAFT };
  let locationError: string | undefined;
  let addingLocation = false;
  let locationAttempt: { signature: string; key: string } | undefined;
  let notice: string | undefined;
  let loadSequence = 0;

  async function refresh(): Promise<void> {
    if (!deps.canAddLocations() && !deps.canListLocations()) {
      root.hidden = true;
      root.replaceChildren();
      return;
    }
    root.hidden = false;
    const sequence = ++loadSequence;
    if (deps.canListLocations()) {
      const result = await listTenantLocations(deps.apiBaseUrl(), deps.tenantId());
      if (!deps.isCurrent() || sequence !== loadSequence) return;
      locations = result.status === 200 ? result.body.locations : [];
    } else {
      locations = [];
    }
    render();
  }

  function reset(): void {
    loadSequence += 1;
    locations = [];
    locationDraft = { ...EMPTY_DRAFT };
    locationError = undefined;
    addingLocation = false;
    locationAttempt = undefined;
    notice = undefined;
    root.hidden = true;
    root.replaceChildren();
  }

  function render(): void {
    root.replaceChildren();
    if (notice !== undefined) {
      root.append(
        el(
          "div",
          {
            cls: "setup-msg setup-msg-info",
            testid: "tablets-location-notice",
            attrs: { role: "status" },
          },
          el("span", { text: notice }),
        ),
      );
    }
    const card = el("section", { cls: "bd-card", testid: "tablets-locations-card" });
    const head = el("div", { cls: "bd-card-head" });
    head.append(el("h3", { cls: "bd-card-title", text: "Locations" }));
    card.append(head);
    if (locations.length === 0) {
      card.append(el("p", { cls: "tablets-empty", text: "No locations yet." }));
    }
    for (const siteLocation of locations) {
      const line = el("div", { cls: "tablets-row", testid: "tablets-location-row" });
      line.dataset["locationId"] = siteLocation.id;
      const text = el("div", { cls: "tablets-row-text" });
      const nameLine = el("div", { cls: "tablets-row-name" });
      nameLine.append(el("span", { text: siteLocation.name, testid: "tablets-location-name" }));
      if (siteLocation.isMain) {
        nameLine.append(el("span", { cls: "tablets-tag", text: "Main location" }));
      }
      text.append(nameLine);
      // LOC3: each location shows its own address; a location with none shows nothing extra.
      if (siteLocation.address !== undefined && siteLocation.address !== null) {
        text.append(
          el("div", {
            cls: "tablets-row-line",
            text: siteLocation.address,
            testid: "tablets-location-address",
          }),
        );
      }
      line.append(text);
      card.append(line);
    }
    root.append(card);
    if (!deps.canAddLocations()) return;

    const fields: Array<[keyof typeof locationDraft, string, string, string]> = [
      ["name", "Name", "tablets-location-name-input", "words"],
      ["line1", "Street", "tablets-location-line1-input", "words"],
      ["city", "City", "tablets-location-city-input", "words"],
      ["state", "State", "tablets-location-state-input", "characters"],
      ["postal", "Postal code", "tablets-location-postal-input", "off"],
    ];
    // AUTOFILL1: the new location's address is its own section, so it is never filled from the signed-in location's
    // address form. The location name is not a person or organisation name and stays unfilled.
    const autofillToken: Record<keyof typeof locationDraft, string> = {
      name: "off",
      line1: "section-new-location address-line1",
      city: "section-new-location address-level2",
      state: "section-new-location address-level1",
      postal: "section-new-location postal-code",
    };
    // LOCSET1 (B10): adding a location is its own card, so its fields never read as a second address form for the
    // location above. The form, its fields, test ids and submit are unchanged.
    const addCard = el("section", { cls: "bd-card", testid: "tablets-add-location-card" });
    const addHead = el("div", { cls: "bd-card-head" });
    addHead.append(el("h3", { cls: "bd-card-title", text: "Add a location" }));
    addCard.append(addHead);
    addCard.append(
      el("p", {
        cls: "bd-help",
        text: "A new location starts clean and takes this customer's tax rate, time zone and tip presets.",
      }),
    );
    const form = el("form", { cls: "tablets-location-form", testid: "tablets-location-form" });
    for (const [key, label, testid, capitalizeMode] of fields) {
      if (key === "state") {
        // VER78: a state is picked, never typed (owner ruling 2026-08-30). The options are the Location address
        // form's own, so the two lists cannot drift.
        const select = el("select", {
          cls: "field-input",
          testid,
          attrs: { id: testid, autocomplete: autofillToken.state },
        });
        bindAutofillSelect(select);
        const source = document.getElementById("admin-settings-address-state");
        for (const option of Array.from(source?.querySelectorAll("option") ?? [])) {
          const copy = document.createElement("option");
          copy.value = option.value;
          copy.textContent = option.value === "" ? "State" : option.textContent;
          select.append(copy);
        }
        select.value = locationDraft.state;
        select.addEventListener("change", () => {
          locationDraft = { ...locationDraft, state: select.value };
        });
        form.append(el("div", { cls: "tablets-field" }, fieldLabel(label, testid), select));
        continue;
      }
      const input = el("input", {
        cls: "field-input",
        testid,
        attrs: {
          type: "text",
          autocomplete: autofillToken[key],
          autocapitalize: capitalizeMode,
          ...(key === "postal" ? { inputmode: "numeric" } : {}),
          id: testid,
        },
      });
      input.value = locationDraft[key];
      input.addEventListener("input", () => {
        locationDraft = { ...locationDraft, [key]: input.value };
      });
      form.append(el("div", { cls: "tablets-field" }, fieldLabel(label, testid), input));
    }
    if (locationError !== undefined) {
      form.append(
        el("p", {
          cls: "admin-error",
          text: locationError,
          testid: "tablets-location-error",
          attrs: { role: "alert" },
        }),
      );
    }
    const submit = el("button", {
      cls: "tablets-primary",
      text: "Add a location",
      testid: "tablets-location-add-button",
      attrs: { type: "submit" },
    });
    submit.disabled = addingLocation;
    form.append(submit);
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitLocation();
    });
    addCard.append(form);
    root.append(addCard);
  }

  function fieldLabel(text: string, forId: string): HTMLElement {
    return el("label", { cls: "field-label", text, attrs: { for: forId } });
  }

  async function submitLocation(): Promise<void> {
    if (addingLocation) return;
    const name = locationDraft.name.trim();
    if (name.length === 0) {
      locationError = "Enter the new location's name.";
      render();
      return;
    }
    const body = {
      name,
      addressLine1: locationDraft.line1.trim() || null,
      city: locationDraft.city.trim() || null,
      state: locationDraft.state.trim() || null,
      postalCode: locationDraft.postal.trim() || null,
    };
    const signature = JSON.stringify(body);
    if (locationAttempt === undefined || locationAttempt.signature !== signature) {
      locationAttempt = { signature, key: deps.newKey() };
    }
    addingLocation = true;
    locationError = undefined;
    notice = undefined;
    render();
    const result = await addLocation(deps.apiBaseUrl(), deps.tenantId(), {
      idempotencyKey: locationAttempt.key,
      ...body,
    });
    addingLocation = false;
    if (!deps.isCurrent()) return;
    if (result.status !== 200 && result.status !== 201) {
      locationError = result.body.message ?? "Could not add the location. Try again.";
      render();
      return;
    }
    locationAttempt = undefined;
    locationDraft = { ...EMPTY_DRAFT };
    notice = `${name} was added.`;
    deps.onLocationsChanged?.();
    await refresh();
  }

  return { refresh, reset };
}
